import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, Edit, Plus, Search, Trash2, Eye, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';
import { Workflow } from '@/types/workflow';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImportLocalWorkflowDialog } from '@/components/workflows/ImportLocalWorkflowDialog';
import { useAuth } from '@/contexts/AuthContext';

const API_PATH = '/workflows';

const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(API_PATH);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch workflows');
      }

      setWorkflows(data.data || []);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;

    try {
      setDeleting(true);
      setError(null);

      const response = await api.delete(`${API_PATH}/${workflowToDelete.id}`);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete workflow');
      }

      setWorkflows(prev => prev.filter(w => w.id !== workflowToDelete.id));
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    } catch (err) {
      console.error('Failed to delete workflow:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete workflow');
    } finally {
      setDeleting(false);
    }
  };

  const handlePublish = async (workflow: Workflow) => {
    try {
      setError(null);

      const response = await api.put(`${API_PATH}/${workflow.id}`, {
        status: workflow.status === 'published' ? 'draft' : 'published'
      });
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to update workflow status');
      }

      setWorkflows(prev => prev.map(w =>
        w.id === workflow.id ? { ...w, status: data.data.status } : w
      ));
    } catch (err) {
      console.error('Failed to update workflow:', err);
      setError(err instanceof Error ? err.message : 'Failed to update workflow status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800 border-green-200';
      case 'draft': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'archived': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'deprecated': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredWorkflows = workflows.filter((workflow) => {
    const matchesStatus = filterStatus === 'all' || workflow.status === filterStatus;
    const matchesSearch = searchQuery === '' ||
                         workflow.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         workflow.slug.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
          <p className="text-muted-foreground">
            Manage workflow definitions and templates
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import guided flow
          </Button>
          <Button onClick={() => navigate('/workflows/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Create Workflow
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex space-x-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="deprecated">Deprecated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflows</CardTitle>
          <CardDescription>
            {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead className="w-[180px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkflows.map((workflow) => (
                  <TableRow key={workflow.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{workflow.title}</div>
                        {workflow.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {workflow.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">
                        {workflow.slug}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(workflow.status)}>
                        {workflow.status.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {workflow.metadata?.isLocalWorkflow || workflow.metadata?.importedFromLocal ? (
                        <Badge variant="outline" className="text-blue-600 border-blue-600">
                          Guided flow
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-600 border-gray-300">
                          Manual
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{workflow.category || 'N/A'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {workflow.tags && workflow.tags.length > 0 ? (
                          workflow.tags.slice(0, 2).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">No tags</span>
                        )}
                        {workflow.tags && workflow.tags.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{workflow.tags.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">v{workflow.version}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{workflow.usageCount || 0}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/workflows/${workflow.id}/edit`)}
                          title="Edit"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePublish(workflow)}
                          title={workflow.status === 'published' ? 'Unpublish' : 'Publish'}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setWorkflowToDelete(workflow);
                            setDeleteDialogOpen(true);
                          }}
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {filteredWorkflows.length === 0 && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No workflows found</h3>
            <p className="text-muted-foreground text-center">
              {workflows.length === 0
                ? "Get started by creating your first workflow."
                : "No workflows match your current filters. Try adjusting your search criteria."
              }
            </p>
            {workflows.length === 0 && (
              <Button className="mt-4" onClick={() => navigate('/workflows/new')}>
                <Plus className="mr-2 h-4 w-4" />
                Create Workflow
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Workflow</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{workflowToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Local Workflow Dialog */}
      <ImportLocalWorkflowDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        userId={user?.uid || ''}
        onSuccess={(imported) => {
          console.log('Imported workflows:', imported);
          fetchWorkflows(); // Refresh the list
        }}
      />
    </div>
  );
};

export default WorkflowsPage;
